<template>
    <div>
      <h3>Create Round Type</h3>
  
      <form @submit.prevent="createRoundType">
        <div class="mb-3">
          <label for="roundName">Round Type Name</label>
          <input
            id="roundName"
            type="text"
            v-model="roundName"
            class="form-control"
            placeholder="Enter round type name"
            required
          />
        </div>
  
        <div class="mb-3">
          <label for="numQuestions">Number of Questions</label>
          <input
            id="numQuestions"
            type="number"
            v-model="numQuestions"
            class="form-control"
            min="1"
            required
          />
        </div>
  
        <!-- Added field to enter the number of correct answers -->
        <div class="mb-3">
          <label for="numCorrectAnswers">Number of Correct Answers</label>
          <input
            id="numCorrectAnswers"
            type="number"
            v-model="numCorrectAnswers"
            class="form-control"
            min="1"
            required
          />
        </div>
  
        <div v-for="(answerType, index) in answerTypes" :key="index" class="mb-3">
          <label :for="'questionType' + index">Question {{ index + 1 }} Answer Type</label>
          <select
            :id="'questionType' + index"
            v-model="answerTypes[index].type"
            class="form-select"
            required
          >
            <option value="text">Text</option>
            <option value="numerical">Numerical</option>
            <option value="select">Select</option>
            <option value="wishbone">Wishbone / Popularity</option>
          </select>
          <label :for="'label' + index">Answer {{ index +1 }} Answer Box Label</label>
          <input
          id="'label' +index"
          type="text"
          v-model="answerTypes[index].label"
          class="form-control"
          />
          
  
          <!-- Conditionally show options input for select type -->
          <div v-if="answerTypes[index].type === 'select'" class="mt-2">
            <label :for="'selectOptions' + index">Enter options (comma-separated)</label>
            <input
              :id="'selectOptions' + index"
              type="text"
              v-model="answerTypes[index].options"
              class="form-control"
              placeholder="Option1, Option2, Option3"
              required
            />
          </div>
        </div>
  
        <div class="mb-3">
          <label for="roundDescription">Round Description</label>
          <textarea
            id="roundDescription"
            v-model="roundDescription"
            class="form-control"
            placeholder="Enter round description"
            required
          ></textarea>
        </div>
  
        <button type="submit" class="btn btn-primary w-100">Create Round Type</button>
      </form>
  
      <!-- List of Created Round Types -->
      <div class="mt-5">
        <h4>Existing Round Types</h4>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Name</th>
              <th>Questions</th>
              <th>Correct Answers</th> <!-- Added column for correct answers -->
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="round in roundTypes" :key="round.id">
              <td>{{ round.roundName }}</td>
              <td>{{ round.numQuestions }}</td>
              <td>{{ round.numCorrectAnswers }}</td> <!-- Display correct answers -->
              <td>{{ round.roundDescription }}</td>
              <td>
                <button @click="editRoundType(round)" class="btn btn-secondary btn-sm">
                  Edit
                </button>
                <button @click="confirmDelete(round)" class="btn btn-danger btn-sm ms-2">
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, onMounted, watch } from 'vue';
  import { db } from '@/firebase';
  import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
  
  export default {
    name: 'CreateRoundType',
    setup() {
      const roundName = ref('');
      const numQuestions = ref(1);
      const numCorrectAnswers = ref(1); // Added correct answers field
      const answerTypes = ref([{ type: 'text', label: 'Answer', options: '' }]);
      const roundDescription = ref('');
      const roundTypes = ref([]);
      const editingRoundId = ref(null);
  
      // Fetch all round types from Firestore
      const fetchRoundTypes = async () => {
        const roundTypesSnapshot = await getDocs(collection(db, 'roundTypes'));
        roundTypes.value = roundTypesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
      };
  
      onMounted(fetchRoundTypes);
  
      const createRoundType = async () => {
        try {
          const preparedAnswerTypes = answerTypes.value.map(q => ({
            type: q.type,
            label: q.label,
            options: q.type === 'select' || q.type === 'wishbone' ? q.options.split(',').map(opt => opt.trim()) : []
          }));
  
          if (editingRoundId.value) {
            // Update existing round type
            const roundDocRef = doc(db, 'roundTypes', editingRoundId.value);
            await updateDoc(roundDocRef, {
              roundName: roundName.value,
              numQuestions: numQuestions.value,
              numCorrectAnswers: numCorrectAnswers.value, // Save correct answers
              answerTypes: preparedAnswerTypes,
              roundDescription: roundDescription.value,
            });
            alert('Round Type updated successfully!');
            editingRoundId.value = null;
          } else {
            // Create new round type
            await addDoc(collection(db, 'roundTypes'), {
              roundName: roundName.value,
              numQuestions: numQuestions.value,
              numCorrectAnswers: numCorrectAnswers.value, // Save correct answers
              answerTypes: preparedAnswerTypes,
              roundDescription: roundDescription.value,
            });
            alert('Round Type created successfully!');
          }
  
          // Reset form fields
          roundName.value = '';
          numQuestions.value = 1;
          numCorrectAnswers.value = 1; // Reset correct answers field
          answerTypes.value = [{ type: 'text', options: '' }];
          roundDescription.value = '';
  
          // Refresh the list of round types
          await fetchRoundTypes();
        } catch (e) {
          console.error('Error creating or updating round type:', e);
        }
      };
  
      const editRoundType = (round) => {
        roundName.value = round.roundName;
        numQuestions.value = round.numQuestions;
        numCorrectAnswers.value = round.numCorrectAnswers; // Load correct answers
        answerTypes.value = round.answerTypes.map(q => ({
          type: q.type,
          options: q.options ? q.options.join(', ') : ''
        }));
        roundDescription.value = round.roundDescription;
        editingRoundId.value = round.id;
      };
  
      const confirmDelete = (round) => {
        if (window.confirm(`Are you sure you want to delete the round type "${round.roundName}"? This action cannot be undone.`)) {
          deleteRoundType(round.id);
        }
      };
  
      const deleteRoundType = async (roundId) => {
        try {
          const roundDocRef = doc(db, 'roundTypes', roundId);
          await deleteDoc(roundDocRef);
          alert('Round Type deleted successfully.');
          await fetchRoundTypes(); // Refresh the list after deletion
        } catch (e) {
          console.error('Error deleting round type:', e);
        }
      };
  
      // Watch numQuestions to update answerTypes array
      watch(numQuestions, (newVal) => {
        if (newVal > answerTypes.value.length) {
          // Add new entries for additional questions
          while (answerTypes.value.length < newVal) {
            answerTypes.value.push({ type: 'text', options: '' });
          }
        } else if (newVal < answerTypes.value.length) {
          // Remove extra entries
          answerTypes.value.splice(newVal);
        }
      });
  
      return {
        roundName,
        numQuestions,
        numCorrectAnswers, // Added to return object
        answerTypes,
        roundDescription,
        roundTypes,
        editingRoundId,
        createRoundType,
        editRoundType,
        confirmDelete,
      };
    },
  };
  </script>
  
  <style scoped>
  /* Add custom styles if needed */
  </style>
